// FILE: /src/Components/Assets/MediaLibrary.js (FULLY UPDATED)

import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, message, Spin, Button, Input } from 'antd'; 
import { fetchAssets, deleteAsset, renameAsset, deleteMultipleAssets } from '../../store/Slices/librarySlice';
import { uploadAssetService } from '../../services/assets';
import Toolbar from './Toolbar';
import AssetGrid from './AssetGrid';
import AssetList from './AssetList'; 
import { DownloadOutlined, EditOutlined, DeleteOutlined, CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { formatBytes } from '../../utils/formatters';
import UpgradeModal from '../BuySubscription/UpgradeModal';
import UploadProgress from './UploadProgress';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

const allowedMimeTypes = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "video/mp4", "video/webm", "video/quicktime",
  // --- THIS IS THE FIX: Add document types ---
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  // --- END OF FIX ---
];
const MAX_FILE_SIZE = 25 * 1024 * 1024;

export default function MediaLibrary() {
  const dispatch = useDispatch();

  const { assets, status, error, totalSize } = useSelector((state) => state.library);
  const { selectedGroup, groups } = useSelector((state) => state.groups);

  const [uploadQueue, setUploadQueue] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [previewState, setPreviewState] = useState({ asset: null, index: -1 });
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [assetToRename, setAssetToRename] = useState(null);
  const [newAssetName, setNewAssetName] = useState('');
  const [upgradeModal, setUpgradeModal] = useState({ visible: false, message: '', featureName: '' });

  const selectedGroupId = useMemo(() => {
    const group = groups.find(g => g.name === selectedGroup);
    return group?._id;
  }, [selectedGroup, groups]);

  useEffect(() => {
    if (selectedGroupId) {
      dispatch(fetchAssets(selectedGroupId));
      setSelectedAssets([]); 
    }
  }, [selectedGroupId, dispatch]);

  const handleFileUpload = (files) => {
    if (!files) return;
    if (!selectedGroupId) {
      message.error("Please select a group before uploading.");
      return;
    }

    const newUploads = Array.from(files)
      .map(file => {
        if (!allowedMimeTypes.includes(file.type)) {
          message.error(`Invalid file type: ${file.name}`);
          return null;
        }
        if (file.size > MAX_FILE_SIZE) {
          message.error(`File is too large: ${file.name} (${formatBytes(file.size)})`);
          return null;
        }
        return {
          id: uuidv4(),
          file,
          progress: 0,
          status: 'uploading',
          error: null,
          source: axios.CancelToken.source(),
        };
      })
      .filter(Boolean);
      
    setUploadQueue(prev => [...prev, ...newUploads]);
    
    newUploads.forEach(uploadItem => {
        uploadAssetService(
            uploadItem.file, 
            selectedGroupId,
            (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadQueue(prev => prev.map(item => item.id === uploadItem.id ? { ...item, progress: percentCompleted } : item));
            }
        )
        .then(() => {
            setUploadQueue(prev => prev.map(item => item.id === uploadItem.id ? { ...item, status: 'completed' } : item));
            dispatch(fetchAssets(selectedGroupId));
        })
        .catch(uploadError => {
            if (uploadError.isLimitError) {
                setUpgradeModal({ visible: true, message: uploadError.message, featureName: uploadError.feature });
            }
            setUploadQueue(prev => prev.map(item => item.id === uploadItem.id ? { ...item, status: 'failed', error: uploadError.message || 'Upload failed' } : item));
        });
    });
  };
  
  const handleClearCompleted = () => {
    setUploadQueue(prev => prev.filter(item => item.status === 'uploading'));
  };

  const handleCloseUploader = () => {
      setUploadQueue([]);
  }

  const handleCancelUpload = (id) => {
      setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  const showPreview = (assetToPreview) => {
    const assetIndex = assets.findIndex(a => a.url === assetToPreview.url);
    if (assetIndex !== -1) {
        setPreviewState({ asset: assetToPreview, index: assetIndex });
    }
  };

  const confirmAndDelete = (assetUrl) => {
    Modal.confirm({
      title: 'Delete Asset',
      content: 'Are you sure you want to delete this asset?',
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        if (!selectedGroupId) return;
        dispatch(deleteAsset({ assetUrl, groupId: selectedGroupId }))
          .unwrap()
          .then(() => {
            message.success("Asset deleted.");
            if (previewState.asset?.url === assetUrl) setPreviewState({ asset: null, index: -1 });
            dispatch(fetchAssets(selectedGroupId)); 
          })
          .catch((err) => message.error(err || "Failed to delete asset."));
      },
    });
  };

  const showRenameModal = (asset) => {
    setAssetToRename(asset);
    const nameWithoutExtension = asset.name.split('.').slice(0, -1).join('.');
    setNewAssetName(nameWithoutExtension);
    setIsRenameModalVisible(true);
  };
  
  const handleRename = () => {
    if (!assetToRename) return;
    dispatch(renameAsset({ oldUrl: assetToRename.url, newName: newAssetName, groupId: selectedGroupId }))
      .unwrap()
      .then((result) => {
        message.success("Asset renamed successfully.");
        if (previewState.asset?.url === assetToRename.url) {
            setPreviewState(prevState => ({ ...prevState, asset: result.newAsset }));
        }
        dispatch(fetchAssets(selectedGroupId));
      })
      .catch((err) => message.error(err.message || "Failed to rename asset."))
      .finally(() => {
        setIsRenameModalVisible(false);
        setAssetToRename(null);
      });
  };
  
  const handleSelectAsset = (assetUrl) => {
    if (Array.isArray(assetUrl)) {
      setSelectedAssets(assetUrl);
      return;
    }
    setSelectedAssets(prev => 
      prev.includes(assetUrl) 
        ? prev.filter(url => url !== assetUrl)
        : [...prev, assetUrl]
    );
  };
  
  const handleSelectAll = () => {
    if (selectedAssets.length === assets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(assets.map(a => a.url));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedAssets.length === 0) return;
    Modal.confirm({
      title: `Delete ${selectedAssets.length} Selected Assets?`,
      content: 'This action cannot be undone.',
      okText: 'Delete All',
      okType: 'danger',
      onOk: () => {
        dispatch(deleteMultipleAssets({ urls: selectedAssets, groupId: selectedGroupId }))
          .unwrap()
          .then(() => {
            message.success(`${selectedAssets.length} assets deleted successfully.`);
            setSelectedAssets([]);
            dispatch(fetchAssets(selectedGroupId));
          })
          .catch((err) => {
            message.error(err || "Some assets could not be deleted.");
          });
      },
    });
  };

  const handleNextAsset = () => {
    const nextIndex = (previewState.index + 1) % assets.length;
    setPreviewState({ asset: assets[nextIndex], index: nextIndex });
  };

  const handlePrevAsset = () => {
    const prevIndex = (previewState.index - 1 + assets.length) % assets.length;
    setPreviewState({ asset: assets[prevIndex], index: prevIndex });
  };

  const renderContent = () => {
    if (status === 'loading' && assets.length === 0) {
      return <div className="flex justify-center items-center h-64"><Spin size="large" /></div>;
    }
    if (status === 'failed') {
      return <div className="text-center text-red-500 p-4 bg-red-50 rounded-lg"><p>Error: {error}</p></div>;
    }
    if (viewMode === 'grid') {
      return (
        <AssetGrid 
          assets={assets} 
          onSelect={handleSelectAsset} 
          selectedAssets={selectedAssets}
          onPreview={showPreview}
        />
      );
    }
    return <AssetList 
        assets={assets} 
        onDelete={confirmAndDelete} 
        onRenameRequest={showRenameModal}
        onSelect={handleSelectAsset}
        selectedAssets={selectedAssets}
        onPreview={showPreview}
    />; 
  };
  
  return (
    <>
      <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-2xl font-bold text-gray-800">Media Library</h4>
          {selectedAssets.length > 0 && (
            <Button type="primary" danger onClick={handleDeleteSelected}>
              Delete ({selectedAssets.length}) Selected
            </Button>
          )}
        </div>

        <Toolbar 
          onFileUpload={handleFileUpload} 
          isUploading={uploadQueue.some(f => f.status === 'uploading')} 
          totalSize={totalSize}
          viewMode={viewMode}
          setViewMode={setViewMode}
          onSelectAll={handleSelectAll}
          areAllSelected={assets.length > 0 && selectedAssets.length === assets.length}
          hasAssets={assets.length > 0}
        />

        {renderContent()}
      </div>

      <UploadProgress 
        uploadQueue={uploadQueue}
        onClearCompleted={handleClearCompleted}
        onCancelUpload={handleCancelUpload}
        onClose={handleCloseUploader}
      />

      <Modal
        open={!!previewState.asset}
        onCancel={() => setPreviewState({ asset: null, index: -1 })}
        footer={null}
        centered
        width="90vw"
        styles={{ body: { padding: 0, height: '90vh' }, content: { padding: 0, backgroundColor: 'transparent' } }}
        destroyOnClose
        closeIcon={<CloseOutlined style={{ color: 'white', fontSize: '20px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '50%', padding: '8px' }} />}
      >
        {previewState.asset && (
          <div className="flex h-full w-full bg-[#1A1A1A]">
            <div className="relative flex-grow flex items-center justify-center p-4">
              {assets.length > 1 && (
                <>
                  <Button shape="circle" icon={<LeftOutlined />} onClick={handlePrevAsset} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 text-white border-none hover:bg-white/40" />
                  <Button shape="circle" icon={<RightOutlined />} onClick={handleNextAsset} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 text-white border-none hover:bg-white/40" />
                </>
              )}
              {previewState.asset.name.match(/\.(mp4|mov|webm)$/i) ? (
                <video src={previewState.asset.url} controls autoPlay className="max-w-full max-h-full" />
              ) : (
                <img src={previewState.asset.url} alt={previewState.asset.name} className="max-w-full max-h-full object-contain" />
              )}
            </div>

            <div className="w-80 bg-white flex-shrink-0 flex flex-col">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-lg text-gray-800">Asset Details</h3>
              </div>
              <div className="flex-grow p-4 space-y-4 overflow-y-auto">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">File Name</label>
                  <p className="text-gray-800 break-words">{previewState.asset.name}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">File Type</label>
                  <p className="text-gray-800">{previewState.asset.name.match(/\.(mp4|mov|webm)$/i) ? 'Video' : 'Image'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">File Size</label>
                  <p className="text-gray-800">{formatBytes(previewState.asset.size)}</p>
                </div>
              </div>
              <div className="p-4 border-t flex flex-wrap gap-2">
                <Button icon={<DownloadOutlined />} href={previewState.asset.url} target="_blank" download={previewState.asset.name} className="flex-1">Download</Button>
                <Button icon={<EditOutlined />} onClick={() => showRenameModal(previewState.asset)} className="flex-1">Rename</Button>
                <Button icon={<DeleteOutlined />} danger onClick={() => confirmAndDelete(previewState.asset.url)} className="flex-1">Delete</Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
      
      <Modal
        title="Rename Asset"
        open={isRenameModalVisible}
        onOk={handleRename}
        onCancel={() => setIsRenameModalVisible(false)}
        okText="Rename"
        destroyOnClose
      >
        <p className="text-sm text-gray-500 mb-2">Enter the new name for the file.</p>
        <Input 
          value={newAssetName}
          onChange={(e) => setNewAssetName(e.target.value)}
          placeholder="Enter new asset name"
          onPressEnter={handleRename}
        />
      </Modal>

      <UpgradeModal
        isOpen={upgradeModal.visible}
        onClose={() => setUpgradeModal({ visible: false, message: '', featureName: '' })}
        message={upgradeModal.message}
        featureName={upgradeModal.featureName}
      />
    </>
  );
}